/**
 * Tools Hook
 * ტულების ჰუკი
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTools,
  getTool,
  createTool,
  updateTool,
  enableTool,
  disableTool,
  updateToolSecrets,
  deleteTool,
  testTool,
  type CreateToolRequest,
  type UpdateToolRequest,
} from '../features/api/tools';

export function useTools() {
  const query = useQuery({
    queryKey: ['tools'],
    queryFn: () => getTools(),
  });

  return {
    tools: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useTool(id: string) {
  const query = useQuery({
    queryKey: ['tool', id],
    queryFn: () => getTool(id),
    enabled: !!id,
  });

  return {
    tool: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreateTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateToolRequest) => createTool(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
    },
  });
}

export function useUpdateTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateToolRequest }) =>
      updateTool(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['tool', id] });
    },
  });
}

export function useEnableTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => enableTool(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['tool', id] });
    },
  });
}

export function useDisableTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => disableTool(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['tool', id] });
    },
  });
}

export function useUpdateToolSecrets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, secrets }: { id: string; secrets: Record<string, string> }) =>
      updateToolSecrets(id, secrets),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['tool', id] });
    },
  });
}

export function useDeleteTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteTool(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
    },
  });
}

export function useTestTool() {
  return useMutation({
    mutationFn: ({ id, parameters }: { id: string; parameters: Record<string, unknown> }) =>
      testTool(id, parameters),
  });
}
